/**
 * Phase 4c — ReportPanel
 *
 * Sidebar panel rendered on the OptimisationStudyDetailPage once a
 * study has reached a terminal state ({"succeeded","partial"}).
 *
 *   • "Generate report" primary button -> POST /studies/{id}/report
 *   • PDF / DOCX download buttons surface once the artefact list
 *     carries the report rows.
 *   • Disabled with tooltip while the study is still running.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import optimisation from "@/api/optimisation";
import { formatApiError } from "@/lib/formatApiError";

const PDF_NAME = "optimisation_report.pdf";
const DOCX_NAME = "optimisation_report.docx";
const TERMINAL = new Set(["succeeded", "partial"]);

function findArt(arts, name) {
    if (!Array.isArray(arts)) return null;
    return arts.find((a) => a && a.name === name) || null;
}

export default function ReportPanel({
    studyId,
    studyStatus,
    artifacts,
    onGenerated,
}) {
    const [pending, setPending] = useState(false);
    const isTerminal = TERMINAL.has((studyStatus || "").toLowerCase());

    const pdfRow = useMemo(() => findArt(artifacts, PDF_NAME), [artifacts]);
    const docxRow = useMemo(() => findArt(artifacts, DOCX_NAME), [artifacts]);
    const lastGeneratedAt = useMemo(() => {
        const candidates = [pdfRow?.updated_at, docxRow?.updated_at]
            .filter(Boolean)
            .map((s) => new Date(s));
        if (!candidates.length) return null;
        return new Date(Math.max(...candidates.map((d) => d.getTime())));
    }, [pdfRow, docxRow]);

    const handleGenerate = async () => {
        setPending(true);
        try {
            await optimisation.generateReport(studyId);
            toast.success("Report generated.");
            if (onGenerated) await onGenerated();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail?.message
                    || formatApiError(e, "Report generation failed"),
            );
        } finally {
            setPending(false);
        }
    };

    return (
        <div
            className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-3 space-y-2"
            data-testid="optimisation-report-panel"
        >
            <h3 className="text-[11px] uppercase tracking-wider text-ux-text-3 flex items-center gap-1.5">
                <FileText className="w-3 h-3" /> Design Optimisation Report
            </h3>
            <p className="text-[11px] text-ux-text-3 leading-snug">
                Procurement-grade summary of this study. Produces matching
                PDF and DOCX artefacts with the recommended configuration,
                Pareto trade-offs and confirmation deltas.
            </p>

            <Button
                size="sm"
                className="w-full mt-1"
                disabled={!isTerminal || pending}
                onClick={handleGenerate}
                data-testid="optimisation-report-generate-button"
                title={
                    isTerminal
                        ? "Render the PDF and DOCX report"
                        : "Report generation requires a completed study."
                }
            >
                {pending ? (
                    <span className="inline-flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Generating…
                    </span>
                ) : pdfRow || docxRow ? (
                    "Regenerate report"
                ) : (
                    "Generate report"
                )}
            </Button>

            {(pdfRow || docxRow) && (
                <div
                    className="space-y-1.5 pt-2 border-t border-ux-border-1"
                    data-testid="optimisation-report-downloads"
                >
                    {pdfRow && (
                        <a
                            href={optimisation.artifactDownloadUrl(studyId, PDF_NAME)}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-between gap-2 px-2 py-1 rounded text-[11px] text-ux-text-2 hover:bg-ux-bg-2 hover:text-ux-text-1"
                            data-testid="optimisation-report-download-pdf"
                        >
                            <span className="flex items-center gap-1.5">
                                <Download className="w-3 h-3" />
                                <span className="font-mono">{PDF_NAME}</span>
                            </span>
                            <span className="tabular-nums text-ux-text-3">
                                {pdfRow.size_bytes
                                    ? `${Math.round(pdfRow.size_bytes / 1024)} kB`
                                    : ""}
                            </span>
                        </a>
                    )}
                    {docxRow && (
                        <a
                            href={optimisation.artifactDownloadUrl(studyId, DOCX_NAME)}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center justify-between gap-2 px-2 py-1 rounded text-[11px] text-ux-text-2 hover:bg-ux-bg-2 hover:text-ux-text-1"
                            data-testid="optimisation-report-download-docx"
                        >
                            <span className="flex items-center gap-1.5">
                                <Download className="w-3 h-3" />
                                <span className="font-mono">{DOCX_NAME}</span>
                            </span>
                            <span className="tabular-nums text-ux-text-3">
                                {docxRow.size_bytes
                                    ? `${Math.round(docxRow.size_bytes / 1024)} kB`
                                    : ""}
                            </span>
                        </a>
                    )}
                    {lastGeneratedAt && (
                        <p
                            className="text-[10px] text-ux-text-3 italic mt-1"
                            data-testid="optimisation-report-last-generated"
                        >
                            Last generated:{" "}
                            {lastGeneratedAt.toISOString().slice(0, 16).replace("T", " ")}
                            {" "}UTC
                        </p>
                    )}
                </div>
            )}

            {!isTerminal && (
                <p
                    className="text-[10px] text-ux-warning italic"
                    data-testid="optimisation-report-not-ready"
                >
                    Report generation requires a completed study.
                </p>
            )}
        </div>
    );
}
